import cn from 'classnames/bind'
import { isEmpty, reduce } from 'ramda'

import { Table, Tr, Th, Td, ThProps } from 'app/common/ui-components/table'
import SimpleDate from 'app/common/classes/simple-date'

import {
  Org,
  TotalItem,
  numberOfFeedingArrange,
} from 'app/dataTypes/reports/data/common/ordersByClasses'

import QuadLineCell from './quad-line-cell'
import styles from './main-table.scss'
import { isValueZero } from 'app/common/templates-next/table-template'

const translate = (key: (typeof numberOfFeedingArrange)[number], val: string) => {
  if (key === 'breakfast') {
    return `${val}\u00A0смена, завтрак`
  }
  if (key === 'dinner') {
    return `${val}\u00A0смена, обед`
  }
  if (key === 'afternoon') {
    return `${val}\u00A0смена, полдник`
  }
  return '' as never
}

const cx = cn.bind(styles)

const roman = (number: number) => ['I', 'II', 'III', 'IV'][number - 1] || String(number)

const shiftTitles = (shifts: Array<number>) => shifts.map((shift) => `${roman(shift)}\u00A0смена`)

const shiftTitlesFeeding = (shifts: Array<number>) =>
  reduce(
    (acc, val) => {
      for (const key of numberOfFeedingArrange) {
        // eslint-disable-line no-restricted-syntax
        const shift = roman(val)
        acc.push(translate(key, shift))
      }
      return acc
    },
    [] as Array<string>,
    shifts,
  )

const renderValue = (val: number | null) => (val === null ? '—' : String(val))

type Props = {
  className?: string
  sameRowClassBase?: string
  data?: Array<Org>
  total?: Array<TotalItem>
  shiftsClasses: Array<number>
  shiftsDayCares: Array<number>
  plannedDate: SimpleDate
  onMouseEnter?: (e: React.MouseEvent) => void
  onMouseLeave?: (e: React.MouseEvent) => void
}

const MainTable = ({
  className,
  sameRowClassBase = 'row_',
  data,
  total,
  shiftsClasses,
  shiftsDayCares,
  plannedDate,
  onMouseEnter,
  onMouseLeave,
}: Props) => {
  const middlePosition = !!data
  const bottomPosition = !!total

  const hasDayCares = !isEmpty(shiftsDayCares)
  const hasClasses = !isEmpty(shiftsClasses)

  const thProps = { scope: 'col', bgFilled: true } as Partial<ThProps>
  const thPropsDowbleHeight = { ...thProps, rowSpan: 2, alignRight: true }
  const thPropsClasses = { ...thProps, colSpan: shiftsClasses.length * 3 }
  const thPropsDayCares = { ...thProps, colSpan: shiftsDayCares.length * 3 }
  const thPropsDayCaresNoFeeding = { ...thProps, colSpan: shiftsDayCares.length }
  const thPropsSub = { ...thProps, alignRight: true }

  const shiftClassName = (hasLeftBorder: boolean) => cx(styles.column, { hasLeftBorder })

  return (
    <Table className={className}>
      <thead>
        <Tr header hidden={middlePosition || bottomPosition}>
          <Th ignoreFirstChild className={styles.column} {...thPropsDowbleHeight}>
            {`По плану на ${plannedDate.getString('DD.MM.YYYY')}, всего`}
          </Th>
          {hasDayCares && (
            <Th className={styles.hasLeftBorder} {...thPropsDayCaresNoFeeding}>
              {'В том числе ГПД'}
            </Th>
          )}
          <Th className={cx(styles.hasLeftBorder, styles.column)} {...thPropsDowbleHeight}>
            {'Получивших рационы питания, всего'}
          </Th>
          {hasClasses && (
            <Th
              className={styles.hasLeftBorder}
              contentClassName={styles.limitedCell}
              {...thPropsClasses}
            >
              {'В том числе за счет средств бюджетов'}
            </Th>
          )}
          {hasDayCares && (
            <Th
              className={styles.hasLeftBorder}
              contentClassName={styles.limitedCell}
              {...thPropsDayCares}
            >
              {'В том числе за счет средств бюджетов при посещении ГПД'}
            </Th>
          )}
          {hasClasses && (
            <Th
              className={styles.hasLeftBorder}
              contentClassName={styles.limitedCell}
              {...thPropsClasses}
            >
              {'За счет средств родителей (законных представителей)'}
            </Th>
          )}
        </Tr>

        <Tr header hidden={middlePosition || bottomPosition}>
          {shiftTitles(shiftsDayCares).map((title, i) => (
            <Th
              {...thPropsSub}
              className={shiftClassName(i === 0)}
              key={`a_${title}`}
              ignoreFirstChild
            >
              {title}
            </Th>
          ))}
          {shiftTitlesFeeding(shiftsClasses).map((title, i) => (
            <Th
              {...thPropsSub}
              className={shiftClassName(i === 0 || i % numberOfFeedingArrange.length === 0)}
              key={`b_${title}`}
            >
              {title}
            </Th>
          ))}
          {shiftTitlesFeeding(shiftsDayCares).map((title, i) => (
            <Th
              {...thPropsSub}
              className={shiftClassName(i === 0 || i % numberOfFeedingArrange.length === 0)}
              key={`c_${title}`}
            >
              {title}
            </Th>
          ))}
          {shiftTitlesFeeding(shiftsClasses).map((title, i) => (
            <Th
              {...thPropsSub}
              className={shiftClassName(i === 0 || i % numberOfFeedingArrange.length === 0)}
              key={`d_${title}`}
            >
              {title}
            </Th>
          ))}
        </Tr>
      </thead>

      {data && (
        <tbody>
          {data.map(({ id, numberOf }, rowIndex) => (
            <Tr
              key={id}
              className={`${sameRowClassBase}${rowIndex}`}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              interlineBorder
            >
              {numberOf.map(({ key, _1_4, _5_9, _10_11, total: tt, hasDelimiter }, i) => (
                <Td
                  key={key}
                  ignoreFirstChild={i === 0}
                  alignRight
                  className={cx({ hasRightBorder: hasDelimiter && i !== numberOf.length - 1 })}
                >
                  <QuadLineCell
                    line1={renderValue(_1_4)}
                    line2={renderValue(_5_9)}
                    line3={renderValue(_10_11)}
                    line4={renderValue(tt)}
                  />
                </Td>
              ))}
            </Tr>
          ))}
        </tbody>
      )}

      {total && (
        <tfoot>
          <Tr>
            {total.map(({ key, value, hasDelimiter }, i) => (
              <Td
                className={cx(styles.column, styles.summary, {
                  hasRightBorder: hasDelimiter && i !== total.length - 1,
                  zero: isValueZero(value),
                })}
                key={key}
                ignoreFirstChild
                alignRight
                bgFilled
              >
                {renderValue(value)}
              </Td>
            ))}
          </Tr>
        </tfoot>
      )}
    </Table>
  )
}

export default MainTable
